// Copyright (c) 2026, Dokos SAS and Contributors
// License: GNU General Public License v3. See license.txt
// @dokos: quick edit dialogs of the Project Management view (Plan mode). A click on a
// project or a task opens its main fields in place; "Edit Full Form" leads to the form.
// Field definitions come from the DocType meta, so labels and options follow it.

import { formatHours, h } from "./PMRender";

const ASSIGN_ADD_METHOD = "frappe.desk.form.assign_to.add";
const ASSIGN_REMOVE_METHOD = "frappe.desk.form.assign_to.remove";

/**
 * @dokos: open the quick edit dialog of a Task.
 * @param {string} name
 * @param {{on_saved?: function}} [opts]
 */
export function openTaskDialog(name, opts = {}) {
	return new PMRecordDialog({ ...TASK_CONFIG, name, ...opts }).show();
}

/**
 * @dokos: open the quick edit dialog of a Project.
 * @param {string} name
 * @param {{on_saved?: function, summary?: {allocated_hours?: number, planned_hours?: number}}} [opts]
 */
export function openProjectDialog(name, opts = {}) {
	return new PMRecordDialog({ ...PROJECT_CONFIG, name, ...opts }).show();
}

const TASK_CONFIG = {
	doctype: "Task",
	title_field: "subject",
	assignees: true,
	layout: [
		["subject"],
		["project", "exp_start_date", "expected_time", "priority"],
		["status", "exp_end_date", "progress"],
		["__assignees", "description"],
	],
};

const PROJECT_CONFIG = {
	doctype: "Project",
	title_field: "project_name",
	assignees: false,
	layout: [
		["project_name"],
		["status", "project_type", "expected_start_date"],
		["customer", "priority", "expected_end_date"],
		["__summary", "notes"],
	],
};

/** @dokos: a dialog field copied from the DocType meta. */
function docfield(doctype, fieldname) {
	const df = frappe.meta.get_docfield(doctype, fieldname);
	if (!df) return null;
	const { fieldtype, label, options, reqd } = df;
	return { fieldname, fieldtype, label, options, reqd };
}

/** @dokos */
class PMRecordDialog {
	constructor({ doctype, name, title_field, assignees, layout, summary = null, on_saved = null }) {
		Object.assign(this, { doctype, name, title_field, assignees, layout, summary, on_saved });
		this.doc = null;
		this.fieldnames = [];
		this.trigger = document.activeElement; // focus goes back here on close
	}

	async show() {
		await frappe.model.with_doctype(this.doctype);
		this.doc = await frappe.db.get_doc(this.doctype, this.name);
		this.make();
		this.dialog.set_values(this.initial_values());
		this.dialog.show();
		return this;
	}

	make() {
		const can_write = frappe.model.can_write(this.doctype);
		this.dialog = new frappe.ui.Dialog({
			// the title is rendered as HTML
			title: frappe.utils.escape_html(
				`${__(this.doctype)} · ${this.doc[this.title_field] || this.name}`
			),
			size: "large",
			fields: this.get_fields(),
			primary_action_label: __("Save"),
			primary_action: () => this.save(),
		});
		this.dialog.$wrapper.addClass("pm-record-dialog");
		this.dialog.add_custom_action(__("Edit Full Form"), () => this.open_full_form());
		if (!can_write) this.dialog.get_primary_btn().prop("disabled", true);

		// Ctrl/Cmd + Enter saves, as in the Task Assignment dialog (Ctrl + S is global)
		this.dialog.$wrapper.on("keydown", (e) => {
			if ((e.ctrlKey || e.metaKey) && e.key === "Enter" && !frappe.request.ajax_count) {
				e.preventDefault();
				this.dialog.get_primary_btn().trigger("click");
			}
		});
		this.dialog.onhide = () => {
			if (this.trigger?.isConnected) this.trigger.focus();
		};
		this.render_summary();
	}

	get_fields() {
		const [head, left, right, tail] = this.layout;
		const fields = [];
		const push = (fieldname) => {
			if (fieldname === "__assignees") {
				fields.push({
					fieldname: "assigned_users",
					fieldtype: "MultiSelectPills",
					label: __("Assigned To"),
					get_data: (txt) =>
						frappe.db.get_link_options("User", txt, { user_type: "System User", enabled: 1 }),
				});
			} else if (fieldname === "__summary") {
				fields.push({ fieldname: "summary", fieldtype: "HTML" });
			} else {
				const df = docfield(this.doctype, fieldname);
				if (!df) return;
				fields.push(df);
				this.fieldnames.push(fieldname);
			}
		};
		head.forEach(push);
		fields.push({ fieldtype: "Section Break" });
		left.forEach(push);
		fields.push({ fieldtype: "Column Break" });
		right.forEach(push);
		fields.push({ fieldtype: "Section Break" });
		tail.forEach(push);
		return fields;
	}

	initial_values() {
		const values = {};
		for (const fieldname of this.fieldnames) values[fieldname] = this.doc[fieldname];
		if (this.assignees) values.assigned_users = this.current_assignees();
		return values;
	}

	current_assignees() {
		try {
			return JSON.parse(this.doc._assign || "[]");
		} catch (e) {
			return [];
		}
	}

	/** @dokos: "34h planned out of 35h of tasks" (Project), from the Plan payload. */
	render_summary() {
		const field = this.dialog.fields_dict.summary;
		if (!field || !this.summary) return;
		const { allocated_hours = 0, planned_hours = 0 } = this.summary;
		const ratio = planned_hours ? Math.min(1, allocated_hours / planned_hours) : 0;
		field.$wrapper.empty().append(
			h(
				"div",
				{ class: "pm-record-summary" },
				h("span", {
					text: __(
						"{0} planned out of {1} of tasks",
						[formatHours(allocated_hours), formatHours(planned_hours)],
						"PM View"
					),
				}),
				h(
					"span",
					{ class: "pm-bar", attrs: { "aria-hidden": "true" } },
					h("span", { class: "pm-bar-fill", style: { width: `${Math.round(ratio * 100)}%` } })
				)
			)
		);
	}

	async save() {
		const values = this.dialog.get_values();
		if (!values) return;
		const changed = {};
		for (const fieldname of this.fieldnames) {
			// loose: "35" (input) equals 35 (doc), null equals ""
			// eslint-disable-next-line eqeqeq
			if ((values[fieldname] ?? "") != (this.doc[fieldname] ?? ""))
				changed[fieldname] = values[fieldname];
		}

		this.dialog.disable_primary_action();
		try {
			if (Object.keys(changed).length) {
				await frappe.xcall("frappe.client.save", { doc: { ...this.doc, ...changed } });
			}
			if (this.assignees) await this.save_assignees(values.assigned_users || []);
			this.dialog.hide();
			frappe.show_alert({ message: __("Saved"), indicator: "green" });
			this.on_saved?.();
		} finally {
			this.dialog.enable_primary_action();
		}
	}

	async save_assignees(users) {
		const before = new Set(this.current_assignees());
		const after = new Set(users);
		const added = [...after].filter((u) => !before.has(u));
		const removed = [...before].filter((u) => !after.has(u));
		const target = { doctype: this.doctype, name: this.name };
		if (added.length) await frappe.xcall(ASSIGN_ADD_METHOD, { ...target, assign_to: added });
		for (const user of removed) {
			await frappe.xcall(ASSIGN_REMOVE_METHOD, { ...target, assign_to: user });
		}
	}

	open_full_form() {
		this.dialog.hide();
		frappe.set_route("Form", this.doctype, this.name);
	}
}
